import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import ColumnFilter from "@/components/ColumnFilter";
import { useColFilter } from "@/lib/tableFilter";
import { Wallet, Gavel, Layers, LayoutDashboard, BarChart3, CalendarClock, FileSpreadsheet, FileText, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const SECTIONS = [
  {
    key: "cari", title: "Cari", color: "#B8933F", icon: Wallet,
    cards: [
      { k: "musteri_sayisi", label: "Müşteri Sayısı", raw: true },
      { k: "toplam_borc", label: "Toplam Borç" },
      { k: "tahsilat", label: "Tahsilat", tone: "#2F6E4E" },
      { k: "kalan", label: "Kalan", tone: "#A63A3A" },
      { k: "pesinat", label: "Peşinat", tone: "#7C5E9B" },
      { k: "toplam_alis", label: "Toplam Alış (AF)" },
      { k: "toplam_satis", label: "Toplam Satış (SF)" },
      { k: "brut_risk", label: "Brüt Risk", tone: "#A63A3A" },
      { k: "net_risk", label: "Net Risk", tone: "#A63A3A" },
    ],
  },
  {
    key: "icra", title: "İcra", color: "#A63A3A", icon: Gavel,
    cards: [
      { k: "dosya_sayisi", label: "Dosya Sayısı", raw: true },
      { k: "devam_eden", label: "Devam Eden Dosya", raw: true, tone: "#26231E" },
      { k: "infaz_sayisi", label: "İnfaz Dosya", raw: true, tone: "#2F6E4E" },
      { k: "asil_alacak", label: "İcra Takibine Giren Tutar" },
      { k: "toplam_alis", label: "Toplam Alış (AF)" },
      { k: "toplam_satis", label: "Toplam Satış (SF)" },
      { k: "tahsilat", label: "Tahsilat", tone: "#2F6E4E" },
      { k: "kalan", label: "Kalan", tone: "#A63A3A" },
      { k: "pesinat", label: "İcra Öncesi Tahsilat", tone: "#7C5E9B" },
      { k: "faiz_tahsilat", label: "Faiz Tahsilatı", tone: "#7C5E9B" },
      { k: "brut_risk", label: "Brüt Risk", tone: "#A63A3A" },
      { k: "net_risk", label: "Net Risk", tone: "#A63A3A" },
    ],
  },
  {
    key: "toplam", title: "Toplam (İkisi Birlikte)", color: "#26231E", icon: Layers,
    cards: [
      { k: "musteri_sayisi", label: "Toplam Kişi", raw: true },
      { k: "toplam_alacak", label: "Toplam Alacak" },
      { k: "tahsilat", label: "Toplam Tahsilat", tone: "#2F6E4E" },
      { k: "kalan", label: "Toplam Kalan", tone: "#A63A3A" },
      { k: "pesinat", label: "Toplam İcra Öncesi Tahsilat", tone: "#7C5E9B" },
    ],
  },
];

const cardStyle = "bg-white border border-[#E7DECC] rounded-sm p-4 shadow-[0_1px_3px_rgba(38,35,30,0.05)] transition-shadow duration-200 hover:shadow-[0_8px_24px_rgba(38,35,30,0.09)]";

export default function Ozet() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("ozet");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    api.get("/ozet/summary").then((res) => {
      const d = res.data || {};
      // İcra "Kalan" = İcra Takibine Giren Tutar (asil_alacak) − Tahsilat
      if (d.icra) d.icra.kalan = (d.icra.asil_alacak || 0) - (d.icra.tahsilat || 0);
      setData(d);
    }).catch(() => setData({}));
  }, []);

  const cari = data?.cari || {};
  const icra = data?.icra || {};
  const toplam = data?.toplam || {};

  const tahsilatKalan = [
    { name: "Cari", Tahsilat: cari.tahsilat || 0, Kalan: cari.kalan || 0 },
    { name: "İcra", Tahsilat: icra.tahsilat || 0, Kalan: icra.kalan || 0 },
  ];
  const riskData = [
    { name: "Cari", "Brüt Risk": cari.brut_risk || 0, "Net Risk": cari.net_risk || 0 },
    { name: "İcra", "Brüt Risk": icra.brut_risk || 0, "Net Risk": icra.net_risk || 0 },
  ];
  const dosyaData = [
    { name: "Devam Eden", value: icra.devam_eden || 0 },
    { name: "İnfaz", value: icra.infaz_sayisi || 0 },
  ];
  const dagilim = [
    { name: "Tahsilat", value: Math.max(toplam.tahsilat || 0, 0) },
    { name: "Kalan", value: Math.max(toplam.kalan || 0, 0) },
  ];
  const tabBtn = (id, label, Icon) => (
    <button onClick={() => setTab(id)} data-testid={`ozet-tab-${id}`}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium border transition-colors duration-150 ${tab === id ? "border-[#26231E] bg-[#26231E] text-white" : "border-[#E7DECC] bg-white text-[#8C8377] hover:bg-[#EFE7D6]"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">GENEL DURUM</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Özet Panel</h1>
          <p className="text-sm text-[#8C8377] mt-2 max-w-2xl">Portföyün genel özeti — Cari, İcra ve toplam rakamlar. (Müşteri/borçlu bazlı detay içermez.)</p>
        </div>
        <div className="flex items-center gap-2">
          {tabBtn("ozet", "Özet", LayoutDashboard)}
          {tabBtn("grafik", "Grafikler", BarChart3)}
          {tabBtn("vadeler", "Cari Vadeler Rapor", CalendarClock)}
          {tabBtn("urun", "Ürün Dağılımı", Layers)}
        </div>
      </div>

      {!data ? (
        <div className="py-24 text-center text-[#8C8377]" data-testid="ozet-loading">Yükleniyor...</div>
      ) : tab === "vadeler" ? (
        <VadelerReport />
      ) : tab === "urun" ? (
        <UrunDagilimi />
      ) : tab === "ozet" ? (
        <div className="space-y-8" data-testid="ozet-board">
          {SECTIONS.map((sec) => {
            const Icon = sec.icon;
            const d = data[sec.key] || {};
            return (
              <div key={sec.key} data-testid={`ozet-section-${sec.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${sec.color}1a` }}>
                    <Icon className="w-4 h-4" style={{ color: sec.color }} />
                  </span>
                  <h2 className="font-heading font-bold text-lg tracking-tight">{sec.title}</h2>
                  <span className="flex-1 h-px" style={{ backgroundColor: `${sec.color}33` }} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {sec.cards.map((c) => {
                    const val = d[c.k];
                    let label = c.label;
                    let tone = c.tone;
                    if (c.k === "net_risk") {
                      if ((val || 0) < 0) { tone = "#2F6E4E"; label = "Net Risk / Kâr"; }
                      else { tone = "#A63A3A"; label = "Net Risk"; }
                    }
                    return (
                      <div key={c.k} className={cardStyle} style={{ borderTop: `3px solid ${sec.color}` }} data-testid={`ozet-${sec.key}-${c.k}`}>
                        <div className="kicker mb-1.5">{label}</div>
                        <div className="font-heading font-black text-xl tabular-nums tracking-tight" style={tone ? { color: tone } : undefined}>
                          {c.raw ? (val ?? 0) : fmt(val)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ozet-charts">
          <ChartCard title="TAHSİLAT vs KALAN (Cari / İcra)">
            <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
              <BarChart data={tahsilatKalan} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7DECC" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: isMobile ? 13 : 12, fill: "#8C8377" }} />
                <YAxis tickFormatter={kfmt} tick={{ fontSize: isMobile ? 12 : 11, fill: "#8C8377" }} width={isMobile ? 46 : 70} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: isMobile ? 13 : 12 }} />
                <Bar dataKey="Tahsilat" fill="#2F6E4E" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Kalan" fill="#B8933F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="RİSK DAĞILIMI (Brüt / Net)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={riskData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7DECC" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: isMobile ? 13 : 12, fill: "#8C8377" }} />
                <YAxis tickFormatter={kfmt} tick={{ fontSize: isMobile ? 12 : 11, fill: "#8C8377" }} width={isMobile ? 46 : 70} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: isMobile ? 13 : 12 }} />
                <Bar dataKey="Brüt Risk" fill="#A63A3A" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Net Risk" fill="#7C5E9B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="İCRA DOSYA DAĞILIMI">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={dosyaData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 82 : 95} label>
                  <Cell fill="#26231E" />
                  <Cell fill="#2F6E4E" />
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: isMobile ? 13 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="TOPLAM TAHSİLAT / KALAN">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={dagilim} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 82 : 95} label={(e) => kfmt(e.value)}>
                  <Cell fill="#2F6E4E" />
                  <Cell fill="#B8933F" />
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: isMobile ? 13 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </Layout>
  );
}

function kfmt(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(1).replace(".", ",") + " Mr";
  if (a >= 1e6) return (n / 1e6).toFixed(1).replace(".", ",") + " Mn";
  if (a >= 1e3) return Math.round(n / 1e3) + " B";
  return String(Math.round(n));
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4">
      <div className="kicker mb-4">{title}</div>
      {children}
    </div>
  );
}


const URUN_SOZLUK = [
  ["COSMIC ORANGE", "KOZMİK TURUNCU"],
  ["SPACE BLACK", "UZAY SİYAHI"],
  ["SPACE GRAY", "UZAY GRİSİ"],
  ["SPACE GREY", "UZAY GRİSİ"],
  ["NATURAL TITANIUM", "NATUREL TİTANYUM"],
  ["BLUE TITANIUM", "MAVİ TİTANYUM"],
  ["WHITE TITANIUM", "BEYAZ TİTANYUM"],
  ["BLACK TITANIUM", "SİYAH TİTANYUM"],
  ["DESERT TITANIUM", "ÇÖL TİTANYUM"],
  ["DEEP PURPLE", "KOYU MOR"],
  ["ALPINE GREEN", "ALPİN YEŞİLİ"],
  ["SIERRA BLUE", "SIERRA MAVİSİ"],
  ["PACIFIC BLUE", "PASİFİK MAVİSİ"],
  ["TITANIUM", "TİTANYUM"],
  ["SILVER", "GÜMÜŞ"],
  ["BLACK", "SİYAH"],
  ["WHITE", "BEYAZ"],
  ["BLUE", "MAVİ"],
  ["RED", "KIRMIZI"],
  ["GREEN", "YEŞİL"],
  ["PINK", "PEMBE"],
  ["PURPLE", "MOR"],
  ["YELLOW", "SARI"],
  ["GOLD", "ALTIN"],
  ["ORANGE", "TURUNCU"],
  ["GRAPHITE", "GRAFİT"],
  ["MIDNIGHT", "GECE YARISI"],
  ["STARLIGHT", "YILDIZ IŞIĞI"],
  ["TEAL", "PETROL YEŞİLİ"],
  ["ULTRAMARINE", "ULTRAMARİN"],
];

function normalizeUrun(raw) {
  let s = (raw || "").trim().replace(/\s+/g, " ").toLocaleUpperCase("tr-TR");
  for (const [en, tr] of URUN_SOZLUK) {
    s = s.replace(new RegExp("(^|\\s)" + en + "(?=\\s|$)", "g"), "$1" + tr);
  }
  return s.replace(/\s+/g, " ").trim();
}

function UrunDagilimi() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/cari").then((r) => setRecords(r.data || [])).catch(() => setRecords([])).finally(() => setLoading(false));
  }, []);

  const custMap = {};
  for (const r of records) {
    const ck = (r.tc_no || "").trim() || (r.musteri || "").trim().toLowerCase();
    if (!custMap[ck]) custMap[ck] = { urun: "", tutar: 0, tahsilat: 0, kalan: 0 };
    const c = custMap[ck];
    c.tutar += r.tutar || 0;
    c.tahsilat += r.odenen || 0;
    c.kalan += r.kalan || 0;
    if (!c.urun && (r.urun || "").trim()) c.urun = normalizeUrun(r.urun);
  }
  const map = {};
  for (const ck in custMap) {
    const c = custMap[ck];
    const key = c.urun || "Belirtilmemiş";
    if (!map[key]) map[key] = { urun: key, musteri: 0, tutar: 0, tahsilat: 0, kalan: 0 };
    const g = map[key];
    g.musteri += 1;
    g.tutar += c.tutar;
    g.tahsilat += c.tahsilat;
    g.kalan += c.kalan;
  }
  const rows = Object.values(map).sort((a, b) => b.musteri - a.musteri);
  const tot = rows.reduce((a, g) => ({ musteri: a.musteri + g.musteri, tutar: a.tutar + g.tutar, tahsilat: a.tahsilat + g.tahsilat, kalan: a.kalan + g.kalan }), { musteri: 0, tutar: 0, tahsilat: 0, kalan: 0 });

  return (
    <div data-testid="ozet-urun-dagilimi">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={cardStyle} data-testid="urun-tur-sayisi"><div className="kicker mb-1.5">Ürün Çeşidi</div><div className="font-heading font-bold text-lg tabular-nums">{rows.length}</div></div>
        <div className={cardStyle} data-testid="urun-toplam-tutar"><div className="kicker mb-1.5">Toplam Satış Tutarı</div><div className="font-heading font-bold text-lg tabular-nums">{fmt(tot.tutar)}</div></div>
        <div className={cardStyle} data-testid="urun-toplam-kalan"><div className="kicker mb-1.5">Toplam Kalan</div><div className="font-heading font-bold text-lg tabular-nums text-[#A63A3A]">{fmt(tot.kalan)}</div></div>
      </div>
      {loading ? (
        <div className="text-[#8C8377] text-sm">Yükleniyor...</div>
      ) : (
        <div className="bg-white border border-[#E7DECC] rounded-sm overflow-x-auto" data-testid="urun-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                <th className="px-4 py-2.5 kicker text-left">Ürün</th>
                <th className="px-4 py-2.5 kicker text-right">Müşteri (Adet)</th>
                <th className="px-4 py-2.5 kicker text-right">Toplam Tutar</th>
                <th className="px-4 py-2.5 kicker text-right">Tahsilat</th>
                <th className="px-4 py-2.5 kicker text-right">Kalan</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#8C8377]" data-testid="urun-empty">Kayıt yok.</td></tr>
              ) : rows.map((g) => (
                <tr key={g.urun} className="border-b border-[#E7DECC]" data-testid={`urun-row-${g.urun}`}>
                  <td className="px-4 py-2 font-medium">{g.urun}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{g.musteri}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(g.tutar)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#2F6E4E]">{fmt(g.tahsilat)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(g.kalan)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                  <td className="px-4 py-2.5">TOPLAM</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{tot.musteri}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt(tot.tutar)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#2F6E4E]">{fmt(tot.tahsilat)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#A63A3A]">{fmt(tot.kalan)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function daysUntil(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

const VADE_PERIODS = [
  { key: "all", label: "Tümü" },
  { key: "today", label: "Bugün" },
  { key: "week", label: "Bu Hafta" },
  { key: "month", label: "Bu Ay + Devreden" },
  { key: "overdue", label: "Gecikmiş" },
];

function inVadePeriod(r, period) {
  const gun = r.gun;
  if (period === "today") return gun === 0;
  if (period === "week") return gun >= 0 && gun <= 7;
  if (period === "month") {
    // İçinde bulunduğumuz TAKVİM AYI + devreden (önceki aylardan kalan gecikmiş bakiye)
    const now = new Date();
    const d = new Date(r.vade_tarihi);
    const sameMonth = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    return sameMonth || (gun < 0 && (r.kalan || 0) > 0);
  }
  if (period === "overdue") return gun < 0 && (r.kalan || 0) > 0;
  return true;
}

function vadeDurum(r) {
  if ((r.tutar || 0) > 0 && (r.kalan || 0) <= 0) return "odendi";
  if (r.gun < 0) return "gecikmis";
  if (r.gun === 0) return "bugun";
  return "gelecek";
}

function VadelerReport() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [yetkili, setYetkili] = useState("");
  const [nameMap, setNameMap] = useState({});
  const [showKarne, setShowKarne] = useState(false);

  useEffect(() => {
    api.get("/cari").then((res) => setRecords(res.data || [])).catch(() => setRecords([])).finally(() => setLoading(false));
    api.get("/users/names").then((res) => {
      const m = {};
      (res.data || []).forEach((u) => { if (u.email) m[u.email.toLowerCase()] = u.name || u.email; });
      setNameMap(m);
    }).catch(() => {});
  }, []);

  const yetkiliAd = (email) => (email ? (nameMap[(email || "").toLowerCase()] || email) : "-");

  const download = async (type) => {
    try {
      const params = { period };
      if (start) params.start = start;
      if (end) params.end = end;
      if (yetkili) params.yetkili = yetkili;
      const res = await api.get(`/cari/vadeler-rapor/export/${type}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "cari_vadeler_raporu.xlsx" : "cari_vadeler_raporu.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Vadeler rapor dışa aktarma başarısız:", e); }
  };

  const withVade = records
    .filter((r) => r.vade_tarihi && r.durum !== "Hukuk Birimi")
    .map((r) => ({ ...r, gun: daysUntil(r.vade_tarihi) }))
    .sort((a, b) => a.gun - b.gun);

  const yetkililer = Array.from(new Set(withVade.map((r) => r.yetkili || "").filter(Boolean))).sort();
  const hasUnassigned = withVade.some((r) => !(r.yetkili || ""));

  const rows = withVade.filter((r) => {
    if (!inVadePeriod(r, period)) return false;
    const d = (r.vade_tarihi || "").slice(0, 10);
    if (start && d < start) return false;
    if (end && d > end) return false;
    if (yetkili === "__none__" && (r.yetkili || "")) return false;
    if (yetkili && yetkili !== "__none__" && (r.yetkili || "").toLowerCase() !== yetkili.toLowerCase()) return false;
    return true;
  });
  const toplamTutar = rows.reduce((s, r) => s + (r.tutar || 0), 0);
  const toplamOdenen = rows.reduce((s, r) => s + (r.odenen || 0), 0);
  const toplamKalan = rows.reduce((s, r) => s + (r.kalan || 0), 0);
  const gecikmeKalan = rows.filter((r) => r.gun < 0 && (r.kalan || 0) > 0).reduce((s, r) => s + (r.kalan || 0), 0);
  const odenmisAdet = rows.filter((r) => (r.tutar || 0) > 0 && (r.kalan || 0) <= 0).length;
  const gecikmisAdet = rows.filter((r) => r.gun < 0 && (r.kalan || 0) > 0).length;
  const oran = toplamTutar > 0 ? Math.round((toplamOdenen / toplamTutar) * 1000) / 10 : 0;

  const karneBase = withVade.filter((r) => {
    if (!inVadePeriod(r, period)) return false;
    const d = (r.vade_tarihi || "").slice(0, 10);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
  const karneMap = {};
  karneBase.forEach((r) => {
    const key = r.yetkili || "";
    const g = karneMap[key] || { tutar: 0, odenen: 0, kalan: 0, gecikme: 0, adet: 0 };
    g.tutar += r.tutar || 0;
    g.odenen += r.odenen || 0;
    g.kalan += r.kalan || 0;
    if (r.gun < 0 && (r.kalan || 0) > 0) g.gecikme += r.kalan || 0;
    g.adet += 1;
    karneMap[key] = g;
  });
  const karneList = Object.entries(karneMap)
    .map(([k, v]) => ({ yetkili: k, ...v, oran: v.tutar > 0 ? Math.round((v.odenen / v.tutar) * 1000) / 10 : 0 }))
    .sort((a, b) => b.odenen - a.odenen);
  const oranColor = (o) => (o >= 70 ? "#2F6E4E" : o >= 40 ? "#B8933F" : "#A63A3A");

  const badge = (r) => {
    const d = vadeDurum(r);
    if (d === "odendi") return <span className="px-2 py-0.5 rounded-sm bg-[#2F6E4E]/10 text-[#2F6E4E] text-xs font-semibold">Ödendi</span>;
    const gun = r.gun;
    if (d === "gecikmis") return <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">{Math.abs(gun)} gün gecikmiş</span>;
    if (d === "bugun") return <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">Bugün</span>;
    if (gun <= 30) return <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">{gun} gün kaldı</span>;
    return <span className="px-2 py-0.5 rounded-sm bg-[#EFE7D6] text-[#8C8377] text-xs font-semibold">{gun} gün</span>;
  };

  const VRCOLS = [
    { key: "musteri", label: "Müşteri", type: "text" },
    { key: "vade_tarihi", label: "Vade Tarihi", type: "date" },
    { key: "yetkili", label: "Yetkili", type: "text", accessor: (r) => yetkiliAd(r.yetkili) },
    { key: "tutar", label: "Tutar", type: "money" },
    { key: "odenen", label: "Ödenen", type: "money" },
    { key: "kalan", label: "Kalan", type: "money" },
  ];
  const { colSel, setColSel, sort, setSort, view, distinct } = useColFilter(VRCOLS, rows);
  const fTutar = view.reduce((s, r) => s + (r.tutar || 0), 0);
  const fOdenen = view.reduce((s, r) => s + (r.odenen || 0), 0);
  const fKalan = view.reduce((s, r) => s + (r.kalan || 0), 0);

  return (
    <div data-testid="ozet-vadeler-report">
      <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div className="flex flex-wrap items-center gap-2" data-testid="vade-report-chips">
          {VADE_PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`vade-report-chip-${p.key}`}
              className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors duration-150 ${period === p.key ? "bg-[#26231E] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"}`}>{p.label}</button>
          ))}
        </div>
        <div>
          <label className="kicker block mb-1">BAŞLANGIÇ</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="vade-report-start" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#26231E]" />
        </div>
        <div>
          <label className="kicker block mb-1">BİTİŞ</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="vade-report-end" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#26231E]" />
        </div>
        <div>
          <label className="kicker block mb-1">YETKİLİ</label>
          <select value={yetkili} onChange={(e) => setYetkili(e.target.value)} data-testid="vade-report-yetkili" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#26231E] bg-white min-w-[180px]">
            <option value="">Tüm Yetkililer</option>
            {yetkililer.map((y) => <option key={y} value={y}>{yetkiliAd(y)}</option>)}
            {hasUnassigned && <option value="__none__">(Atanmamış)</option>}
          </select>
        </div>
        {(start || end) && <button onClick={() => { setStart(""); setEnd(""); }} data-testid="vade-report-clear" className="px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A]">Tarihi Temizle</button>}
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={() => download("excel")} data-testid="vade-report-excel" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><FileSpreadsheet className="w-4 h-4 text-[#2F6E4E]" /> Excel</button>
          <button onClick={() => download("pdf")} data-testid="vade-report-pdf" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><FileText className="w-4 h-4 text-[#A63A3A]" /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="vade-report-adet"><div className="kicker mb-1.5">Taksit Sayısı</div><div className="font-heading font-bold text-lg tabular-nums">{rows.length}</div><div className="text-xs text-[#8C8377] mt-1">{odenmisAdet} ödendi · {gecikmisAdet} gecikmiş</div></div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="vade-report-tutar"><div className="kicker mb-1.5">Toplam Taksit Tutarı</div><div className="font-heading font-bold text-lg tabular-nums">{fmt(toplamTutar)}</div></div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="vade-report-odenen"><div className="kicker mb-1.5">Ödenen</div><div className="font-heading font-bold text-lg tabular-nums text-[#2F6E4E]">{fmt(toplamOdenen)}</div></div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="vade-report-gecikme"><div className="kicker mb-1.5">Gecikmede</div><div className="font-heading font-bold text-lg tabular-nums text-[#A63A3A]">{fmt(gecikmeKalan)}</div><div className="text-xs text-[#8C8377] mt-1">Toplam kalan: {fmt(toplamKalan)}</div></div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="vade-report-oran"><div className="kicker mb-1.5">Tahsilat Oranı</div><div className="font-heading font-bold text-lg tabular-nums" style={{ color: oranColor(oran) }}>%{oran.toLocaleString("tr-TR")}</div><div className="text-xs text-[#8C8377] mt-1">ödenen ÷ tutar</div></div>
      </div>

      <div className="mb-6">
        <button onClick={() => setShowKarne((s) => !s)} data-testid="vade-report-karne-toggle" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
          <Users className="w-4 h-4 text-[#26231E]" /> Yetkili Karnesi {showKarne ? "▲" : "▼"}
        </button>
        {showKarne && (
          <div className="mt-3 bg-white border border-[#E7DECC] rounded-sm overflow-hidden" data-testid="vade-report-karne">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10">
                  <tr>
                    <th className="px-3 py-2.5 kicker text-left">Yetkili</th>
                    <th className="px-3 py-2.5 kicker text-right">Taksit</th>
                    <th className="px-3 py-2.5 kicker text-right">Tutar</th>
                    <th className="px-3 py-2.5 kicker text-right">Ödenen</th>
                    <th className="px-3 py-2.5 kicker text-right">Gecikmede</th>
                    <th className="px-3 py-2.5 kicker text-right">Tahsilat Oranı</th>
                  </tr>
                </thead>
                <tbody>
                  {karneList.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8C8377]" data-testid="vade-report-karne-empty">Kayıt yok.</td></tr>
                  ) : karneList.map((k) => (
                    <tr key={k.yetkili || "none"} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`vade-report-karne-row-${k.yetkili || "none"}`}>
                      <td className="px-3 py-2 font-medium">{k.yetkili ? yetkiliAd(k.yetkili) : "(Atanmamış)"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{k.adet}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(k.tutar)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{fmt(k.odenen)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(k.gecikme)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: oranColor(k.oran) }}>%{k.oran.toLocaleString("tr-TR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-[#8C8377] text-sm">Yükleniyor...</div>
      ) : (
        <div className="bg-white border border-[#E7DECC] rounded-sm overflow-x-auto" data-testid="vade-report-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                {VRCOLS.map((c) => (
                  <th key={c.key} className={`px-3 py-2.5 kicker ${c.type === "money" ? "text-right" : "text-left"}`}>
                    <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                      <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                      <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2.5 kicker text-left">Durum</th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]" data-testid="vade-report-empty">Seçilen aralıkta vade yok.</td></tr>
              ) : view.map((r) => (
                <tr key={r.id} className="border-b border-[#E7DECC]" data-testid={`vade-report-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.musteri}</td>
                  <td className="px-3 py-2 tabular-nums">{fmtDate(r.vade_tarihi)}</td>
                  <td className="px-3 py-2 text-[#8C8377]">{yetkiliAd(r.yetkili)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.tutar)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{fmt(r.odenen)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${(r.kalan || 0) > 0 ? "text-[#A63A3A]" : "text-[#8C8377]"}`}>{fmt(r.kalan)}</td>
                  <td className="px-3 py-2">{badge(r)}</td>
                </tr>
              ))}
            </tbody>
            {view.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                  <td className="px-3 py-2.5" colSpan={3}>FİLTRELİ TOPLAM ({view.length})</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(fTutar)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#2F6E4E]">{fmt(fOdenen)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#A63A3A]">{fmt(fKalan)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
